import React from 'react';
import {useTranslation} from 'react-i18next';

import {ActionButton, Modals} from '@edna/components';
import {PrimitiveContainer} from '@edna/components/Modals';
import {Icon, Stack} from '@edna/components/primitives';

import {AdvertisingIcon, QuestionMarkIcon, SmsIcon} from 'src/icons';

import {
  ChatCenterWidget,
  showChatCenterWidget,
  useChatCenterWidgetSourceCode,
} from './ChatCenterWidget';
import {
  ChatCenterWidgetContextProvider,
  useChatCenterWidgetState,
} from './ChatCenterWidget/ChatCenterState';
import {FeedbackWidgetButton, showFeedbackWidget} from './FeedbackWidget';
import {MODALS_CONTAINER_ID} from './definitions';
import * as S from './style';

const HelpButton = React.memo(() => {
  const {t} = useTranslation();
  const [isHelpOpen, setIsHelpOpen] = React.useState(false);
  const [isFeedbackOpen, setIsFeedbackOpen] = React.useState(false);

  const options = React.useMemo(() => {
    return [
      {
        content: (
          <Stack direction="ROW" size="2" align="center">
            <Icon as={SmsIcon} size="20px" />
            <span>{t('HelpWidget:chat')}</span>
          </Stack>
        ),
        handler: showChatCenterWidget,
      },
      {
        content: (
          <Stack direction="ROW" size="2" align="center">
            <Icon as={AdvertisingIcon} size="20px" />
            <span>{t('HelpWidget:feedback')}</span>
          </Stack>
        ),
        handler: () => {
          showFeedbackWidget(() => setIsFeedbackOpen(false));
          setIsFeedbackOpen(true);
        },
      },
    ];
  }, [t]);

  const {unreadCount, isOpen: isChatCenterWidgetOpen} = useChatCenterWidgetState();
  const isOpen = isHelpOpen || isChatCenterWidgetOpen || isFeedbackOpen;

  return (
    <ActionButton
      disabled={isOpen}
      onOpen={() => setIsHelpOpen(true)}
      onClose={() => setIsHelpOpen(false)}
      head={
        <S.WidgetButton isOpen={isOpen}>
          <Icon as={QuestionMarkIcon} size="32px" />
          {unreadCount > 0 && <S.Counter>{unreadCount}</S.Counter>}
        </S.WidgetButton>
      }
      dropdownComponent={S.ActionButtonDropdown}
      optionComponent={S.ActionButtonOption}
      options={options}
      overlayOffsetY={10}
    />
  );
});

const HelpWidget = React.memo(() => {
  const chatCenterWidgetSourceCode = useChatCenterWidgetSourceCode();

  const ui = (
    <>
      {chatCenterWidgetSourceCode && (
        <>
          <HelpButton />
          <ChatCenterWidget scriptCode={chatCenterWidgetSourceCode} />
        </>
      )}
      {!chatCenterWidgetSourceCode && <FeedbackWidgetButton />}
      <Modals
        isUpdateOverflow={false}
        containerId={MODALS_CONTAINER_ID}
        container={PrimitiveContainer}
      />
    </>
  );

  if (chatCenterWidgetSourceCode) {
    return <ChatCenterWidgetContextProvider>{ui}</ChatCenterWidgetContextProvider>;
  }

  return ui;
});

export {HelpWidget};
